// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import breakpoints from "@guestsupply/XDL/PreStyle/breakpoints";
import systemZIndex from "@guestsupply/XDL/PreStyle/zIndex";
import groupBy from "lodash/groupBy";

/**
 * To load JS script with callback.
 * you can pass one url or multiple
 * Example:
 * getScripts.urls([ // url1, url2]).then(() => { //callback handler });
 */
export const getScripts = {
    url: (url: string) => {
        return new Promise((resolve, reject) => {
            if (typeof document !== "undefined") {
                const script = document.createElement("script");
                script.type = "text/javascript";
                script.src = url;
                script.addEventListener("load", () => resolve(script), false);
                script.addEventListener("error", () => reject(script), false);
                document.body.appendChild(script);
            }
        });
    },
    urls: (urls: string[]) => {
        return Promise.all(urls.map(getScripts.url));
    },
};

export const isSmallMobileScreen = () =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 480px)").matches;

export const isMobileScreen = () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;

export const isTabletScreen = () => typeof window !== "undefined" && window.matchMedia("(max-width: 1024px)").matches;

export const isWideScreen = () => typeof window !== "undefined" && window.matchMedia("(min-width: 2200px)").matches;

/**
 * To convert string to boolean
 * @param value
 */
export const parseBoolean = (value?: string) => (value ? value?.toLowerCase() === "true" : false);

/**
 * split without empty or undefined values
 * @param value
 */
export const flatSplit = (value?: string, target = "") => (value || "").split(target).filter(item => item) || [];

/**
 * Method use to pass for props not accept undefined
 */
export const doNothing = () => {};

/**
 * Helper function to export data to csv file
 * @param fields
 * @param data
 * @param filename
 */
export const exportToCSV = async (fields: string[], data: (string | number | null)[][], filename?: string) => {
    const dataForUnparse = {
        fields,
        data,
    };

    const { unparse } = await import(/* webpackChunkName: "papaparse" */ "papaparse");
    const csv = unparse(dataForUnparse);
    const csvBlob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
    const fileName = `${filename || "csv_exported_file"}_${Math.random() * 10000}.csv`;
    const downloadLink = document.createElement("a");
    downloadLink.href = window.URL.createObjectURL(csvBlob);
    downloadLink.setAttribute("download", fileName);
    downloadLink.click();
};

export const getSystemZIndex = (key: string): number => {
    const zIndexValues = systemZIndex as any;
    return zIndexValues[key] || -1;
};

export const generateSlidesPerViewMap = (opts?: { [key: number]: number }) => {
    const { maxWidths } = breakpoints;
    const values: any = {};
    if (!opts) {
        values[maxWidths[0]] = {
            slidesPerView: 1,
        };
        values[maxWidths[1]] = {
            slidesPerView: 2,
        };
        values[maxWidths[2]] = {
            slidesPerView: 3,
        };
        values[maxWidths[3]] = {
            slidesPerView: 4,
        };
        return values;
    }

    Object.keys(opts).forEach(key => {
        if (!maxWidths[+key]) {
            return;
        }
        values[maxWidths[+key]] = {
            slidesPerView: +opts[+key],
        };
    });
    return values;
};

export const stringToKeyGenerator = (str: string) => (str ? str.replace(/ +/g, "") : "");

export const groupSortBy = (
    array: any[],
    groupByField: string | number,
    sort1: string | number,
    sort2: string | number,
) => {
    const lineGroups = groupBy(array, line => line.properties[groupByField]);

    // Sort lines inside each category
    Object.keys(lineGroups).forEach(key => {
        lineGroups[key].sort((a, b) => (a.properties[sort2] > b.properties[sort2] ? 1 : -1));
    });

    // Convert to array and sort categories based on sort1
    const groupKeys = Object.keys(lineGroups).sort((groupKey1, groupKey2) => {
        if (groupKey2 === "undefined") {
            return -1;
        }
        if (groupKey1 === "undefined") {
            return 1;
        }
        return lineGroups[groupKey1][0].properties[sort1] - lineGroups[groupKey2][0].properties[sort1];
    });

    const groupArrays = groupKeys.map(key => lineGroups[key]);

    return groupArrays;
};

export const isURL = (value: string) => {
    const urlRegex = /^(ftp|http|https):\/\/[^ "]+$/;
    return urlRegex.test(value);
};

type Category = {
    name: string;
    id: string;
};

type ParsedCategories = {
    [key: string]: Category;
};

export function parseProductCategories(categories: string | undefined, includeListData = true) {
    // Split the categories by underscore
    const items = categories
        ?.split("_")
        .map(item => item.trim())
        .filter(item => item !== "");

    // Split each item by pipe (|) to get the name and id
    const parsedCategories: ParsedCategories = {};
    items?.forEach((item, index) => {
        const [name, id] = item.split("|").map(part => part.trim());
        const categoryNumber = items.length - index;
        parsedCategories[`category_${categoryNumber}`] = { name, id };
    });

    let categoriesData = {
        item_category: parsedCategories?.["category_1"]?.name,
        item_category2: parsedCategories?.["category_2"]?.name,
        item_category3: parsedCategories?.["category_3"]?.name,
        item_category4: parsedCategories?.["category_4"]?.name,
        item_category5: parsedCategories?.["category_5"]?.name,
    };

    if (includeListData) {
        categoriesData = {
            ...categoriesData,
            item_list_id: parsedCategories?.["category_1"]?.id,
            item_list_name: parsedCategories?.["category_1"]?.name,
        } as any;
    }

    return {
        ...categoriesData,
    };
}
